import { Button } from 'components/button';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { db, downloadUrls, storage, useAuthState } from 'firebaseStore';
import {
    Column,
    ImageField,
    UserProfile,
    userProfileCoverter,
    USER_PROFILE_COLLECTION_NAME,
} from 'models';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import Modal from 'react-modal';
import { Confirm, ConfirmProps, customStyles, InputMultiple } from 'components/settings/recordList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useUserProfile } from 'components/settings/userProfileContext';
import { useCommit } from 'commits';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import './usersPage.css';

Modal.setAppElement('#modal');

export const usePathUrl = (path?: string) => {
    const [url, setUrl] = useState<string>();
    if (path) {
        const downloadurl = downloadUrls.get(path);
        if (downloadurl) {
            return downloadurl;
        }
        getDownloadURL(ref(storage, path)).then((newUrl) => {
            downloadUrls.set(path, newUrl);
            setUrl(newUrl);
        });
    }
    return url;
};

const ProfileImage = ({
    userProfile,
    side,
    blob,
    setBlob,
}: {
    userProfile: UserProfile;
    side: string;
    blob?: Blob | null;
    setBlob?: (_: Blob | null) => void;
}) => {
    const downloadUrl = usePathUrl(userProfile.profileImages?.[0]?.path);
    const onDrop = useCallback(
        (acceptedFiles?: File[]) => {
            if (!setBlob) {
                throw new Error('Impossible: no setBlob');
            }
            if (!acceptedFiles) {
                throw new Error('No file selected');
            }
            if (acceptedFiles.length === 0) {
                throw new Error('Cannot handle multiple files');
            }
            const imageFile = acceptedFiles[0];
            if (imageFile.type !== 'image/jpeg') {
                throw new Error('Can only handle JPEGs');
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                const image = document.createElement('img');
                image.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.setAttribute('width', '170px');
                    canvas.setAttribute('height', '170px');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        throw new Error('no ctx');
                    }
                    ctx.drawImage(image, 0, 0, 170, 170);
                    canvas.toBlob((newBlob) => setBlob(newBlob), 'image/jpeg');
                };
                image.src = e.target?.result as string;
            };
            reader.readAsDataURL(imageFile);
        },
        [setBlob]
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
    const alt = userProfile.profileImagePath || 'profile image';

    const src = blob ? URL.createObjectURL(blob) : downloadUrl || 'Ellipse4.png';
    if (!setBlob) {
        return (
            <img
                id={`editor:${userProfile.email}`}
                alt={alt}
                src={src}
                height={side}
                width={side}
                style={{
                    borderRadius: side,
                    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                }}
            />
        );
    }
    return (
        <div
            {...getRootProps()}
            style={{
                width: '190px',
                height: '190px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '190px',
                transition: 'border-color 0.25s',
                borderStyle: 'dashed',
                borderWidth: '3px',
                paddingTop: '3px',
                ...(isDragActive ? { borderColor: 'black' } : { borderColor: 'white' }),
            }}
        >
            <input {...getInputProps()} />
            <img
                alt={alt}
                src={src}
                height={side}
                width={side}
                style={{
                    borderRadius: side,
                    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                }}
            />
        </div>
    );
};

function useOnClickOutside(ref: RefObject<HTMLDivElement>, handler: (e: MouseEvent) => void) {
    // Inspired by https://usehooks.com/useOnClickOutside/
    useEffect(() => {
        const listener = (e: any) => {
            if (!ref.current || ref.current.contains(e.target)) {
                return;
            }
            handler(e);
        };
        document.addEventListener('click', listener);
        return () => document.removeEventListener('click', listener);
    }, [ref, handler]);
}

const FloatingMenu = ({
    open,
    setOpen,
    onDelete,
    onEdit,
    isMe,
}: {
    isMe: boolean;
    open: boolean;
    setOpen: (_: boolean) => void;
    onDelete: () => void;
    onEdit: () => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    useOnClickOutside(ref, () => setOpen(false));
    return open ? (
        <div
            ref={ref}
            style={{
                position: 'absolute',
                top: '-22px',
                right: '16px',
                height: '91px',
                width: '156px',
                border: '1px solid #979797',
                backgroundColor: 'white',
                display: 'flex',
                cursor: 'pointer',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    paddingLeft: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    cursor: 'pointer',
                    borderBottom: '1px solid #979797',
                }}
                onClick={() => {
                    setOpen(false);
                    onEdit();
                }}
            >
                Edit
            </div>
            <div
                title={isMe ? 'You cannot delete yourself' : undefined}
                style={{
                    paddingLeft: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    cursor: isMe ? undefined : 'pointer',
                }}
                onClick={() => {
                    if (isMe) {
                        return;
                    }
                    setOpen(false);
                    onDelete();
                }}
            >
                Delete
            </div>
        </div>
    ) : (
        <div
            style={{
                position: 'absolute',
                top: '-22px',
                right: '16px',
                height: '91px',
                width: '156px',
            }}
        ></div>
    );
};

interface Roles {
    [key: string]: string;
}
const ROLES: Roles = { admin: 'admin', user: 'standard user', reviewer: 'reviewer' };

const emailRe = /\S+@\S+\.\S+/;

export const UsersPage = () => {
    const [me] = useUserProfile();
    if (!me?.admin()) {
        return <div></div>;
    }
    return (
        <CollectionProvider
            collectionName={USER_PROFILE_COLLECTION_NAME}
            converter={userProfileCoverter}
            keyFunction={(r) => r.id}
        >
            <UsersPageContent />
        </CollectionProvider>
    );
};

const userProfileColumns: Column[] = [
    {
        field: 'name',
        label: 'name',
        input: true,
        required: true,
    },
    {
        field: 'email',
        label: 'email',
        input: true,
        required: true,
    },
    {
        field: 'roles',
        label: 'Role',
        input: true,
        required: true,
        source: 'oneOf',
    },
];

const ProfileItem = ({
    userProfile,
    onDelete,
    onEdit,
}: {
    userProfile: UserProfile;
    onDelete: () => void;
    onEdit: () => void;
}) => {
    const [me] = useUserProfile();
    const isMe = me?.email === userProfile.email;
    if (isMe) {
        console.log('me', userProfile);
    }
    const [open, setOpen] = useState(false);
    return (
        <div
            style={{
                height: '144px',
                width: '379px',
                margin: '0 27px 39px 27px',
                display: 'flex',
                position: 'relative',
                boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.2)',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '24px',
                    left: '14px',
                    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
                }}
            >
                <ProfileImage side="95px" userProfile={userProfile} />
            </div>
            <FloatingMenu
                open={open}
                setOpen={setOpen}
                onDelete={onDelete}
                onEdit={onEdit}
                isMe={isMe}
            />
            <div style={{ width: '100%' }}>
                <div
                    style={{
                        width: 'calc(100% - 20px)',
                        paddingRight: '20px',
                        alignItems: 'center',
                        display: 'flex',
                        backgroundColor: '#FBFBFB',
                        height: '50px',
                        fontSize: '30px',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ flexBasis: '128px', flexShrink: 1 }}></div>
                    <div
                        style={{
                            flexBasis: '231px',
                            fontSize: '30px',
                            whiteSpace: 'nowrap',
                            flexGrow: 1,
                        }}
                    >
                        {userProfile.name}
                    </div>
                    <MoreVertIcon
                        fontSize="small"
                        style={{
                            cursor: 'pointer',
                            position: 'absolute',
                            right: '-2px',
                            top: '17px',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            return setOpen(!open);
                        }}
                    />
                </div>
                <div
                    style={{
                        marginLeft: '128px',
                        width: 'calc(100% - 128px)',
                        marginTop: '6px',
                        height: '20px',
                    }}
                >
                    {userProfile.title}
                </div>
                <div style={{ marginLeft: '128px', width: 'calc(100% - 128px)', marginTop: '0px' }}>
                    {userProfile.email}
                </div>
                <div style={{ marginLeft: '128px', marginTop: '0px', width: 'calc(100% - 128px)' }}>
                    {userProfile.roles.map((role) => ROLES[role]).join(', ')}
                </div>
            </div>
        </div>
    );
};

const UsersPageContent = () => {
    const [, , tenant] = useAuthState();
    const [userProfiles, , , userProfileMap] = useCollectionContext(
        USER_PROFILE_COLLECTION_NAME
    ) as CollectionState<UserProfile>;
    const [newUserProfile, setNewUserProfile] = useState(new UserProfile({ tenantId: tenant }));
    const [modalIsOpen, _setModalIsOpen] = useState(false);
    const setModalIsOpen = (modalOpen: boolean) => {
        if (modalOpen) {
            _setModalIsOpen(true);
        } else {
            setNewUserProfile(new UserProfile({ tenantId: tenant }));
            _setModalIsOpen(false);
        }
    };
    const [confirmProps, setConfirmProps] = useState<ConfirmProps>();
    const collectionRef = collection(db, USER_PROFILE_COLLECTION_NAME).withConverter(
        userProfileCoverter
    );
    const commit = useCommit<UserProfile>();
    const onSave = async (blob?: Blob | null) => {
        const missing = userProfileColumns.find(
            (column) => column.required && !newUserProfile[column.field]
        );
        if (missing) {
            return Promise.reject(missing);
        }
        if (!emailRe.test(newUserProfile.email)) {
            return Promise.reject(missing);
        }
        newUserProfile.name = newUserProfile.name.trim();
        newUserProfile.title = newUserProfile.title.trim();
        newUserProfile.email = newUserProfile.email.toLowerCase().trim();

        if (newUserProfile.id) {
            const previous = userProfileMap.get(newUserProfile.id);
            if (!previous) {
                throw new Error(
                    `Impossible! Updating non-extistent user profile ${newUserProfile.id}`
                );
            }
            if (blob) {
                const path = `${newUserProfile.tenantId}::${newUserProfile.id}::profileImage::170x170::v0`;
                const storageRef = ref(storage, path);
                await uploadBytes(storageRef, blob);
                const newProfileImages: ImageField[] = [
                    {
                        path,
                        height: '170',
                        width: '170',
                        fileType: 'image/jpeg',
                        version: 'v0',
                    },
                ];
                newUserProfile.profileImages = newProfileImages.concat(
                    newUserProfile.profileImages
                );
            }
            await commit({
                current: newUserProfile,
                previous: previous,
                action: 'update',
                message: '',
            });
            return updateDoc(
                doc(db, USER_PROFILE_COLLECTION_NAME, newUserProfile.id),
                new UserProfile({ ...newUserProfile, id: newUserProfile.id }).toFirestore()
            ).then(
                () => setModalIsOpen(false),
                (error) => {
                    console.error('Update updateDoc error', error);
                    setModalIsOpen(false);
                }
            );
        }
        return addDoc(collectionRef, newUserProfile).then(
            async (newDocRef) => {
                if (blob) {
                    const path = `${newUserProfile.tenantId}::${newDocRef.id}::profileImage::170x170::v0`;
                    const storageRef = ref(storage, path);
                    await uploadBytes(storageRef, blob);
                    const newProfileImages: ImageField[] = [
                        {
                            path,
                            height: '170',
                            width: '170',
                            fileType: 'image/jpeg',
                            version: 'v0',
                        },
                    ];
                    newUserProfile.profileImages = newProfileImages.concat(
                        newUserProfile.profileImages
                    );
                }
                return updateDoc(
                    newDocRef,
                    new UserProfile({ ...newUserProfile, id: newDocRef.id }).toFirestore()
                ).then(
                    () => setModalIsOpen(false),
                    (error) => {
                        console.error('Save updateDoc error', error);
                        setModalIsOpen(false);
                    }
                );
            },
            (error) => {
                console.error('addDoc error', error);
                setModalIsOpen(false);
            }
        );
    };
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '0 108px',
            }}
        >
            <Modal
                isOpen={!!modalIsOpen}
                style={customStyles(2, {
                    overlay: { backgroundColor: 'rgb(27 27 27 / 50%)' },
                })}
                onRequestClose={() => setModalIsOpen(false)}
            >
                {
                    <AddUser
                        userProfile={newUserProfile}
                        setNewUserProfile={setNewUserProfile}
                        setModalIsOpen={setModalIsOpen}
                        onSave={onSave}
                    />
                }
            </Modal>
            <Modal
                isOpen={!!confirmProps}
                style={customStyles(4)}
                onRequestClose={() => setConfirmProps(undefined)}
            >
                {confirmProps ? <Confirm {...confirmProps} /> : null}
            </Modal>
            <div style={{ fontSize: '34px' }}>User Management</div>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '20px',
                }}
            >
                <Button
                    style={{ width: '149px', fontSize: '16px' }}
                    onClick={() => setModalIsOpen(true)}
                >
                    Add user
                </Button>
            </div>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                {userProfiles.map((userProfile) => (
                    <ProfileItem
                        userProfile={userProfile}
                        key={userProfile.email}
                        onEdit={() => {
                            setNewUserProfile(userProfile);
                            setModalIsOpen(true);
                        }}
                        onDelete={() => {
                            return setConfirmProps({
                                title: `You are about to delete ${userProfile.name} ${userProfile.email}`,
                                text: `Are you sure you want to continue?`,
                                onClick: () => {
                                    deleteDoc(doc(collectionRef, userProfile.id));
                                    setConfirmProps(undefined);
                                },
                                setModalIsOpen: () => setConfirmProps(undefined),
                                actionLabel: 'continue',
                                withMessage: true,
                            });
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

interface AddUserProps {
    userProfile: UserProfile;
    onSave: (_?: Blob | null) => void;
    setModalIsOpen: (_: boolean) => void;
    setNewUserProfile: (_: UserProfile) => void;
}
const AddUser = ({ userProfile, setModalIsOpen, setNewUserProfile, onSave }: AddUserProps) => {
    const [, , tenant] = useAuthState();
    const [blob, setBlob] = useState<Blob | null>();
    return (
        <div style={{ margin: '36px' }}>
            <div
                style={{
                    fontSize: '30px',
                    backgroundColor: '#FBFBFB',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {userProfile.id ? 'Edit User' : 'Add User'}
            </div>
            <div
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <div style={{ marginTop: '-32px' }}>
                    <ProfileImage
                        userProfile={userProfile}
                        side="170px"
                        setBlob={setBlob}
                        blob={blob}
                    />
                </div>
                <div style={{ fontSize: '20px' }}>Upload image</div>
            </div>
            <div style={{ height: '20px', margin: '15px 0 5px 0' }}>Full Name*</div>
            <input
                onChange={(e) =>
                    setNewUserProfile(new UserProfile({ ...userProfile, name: e.target.value }))
                }
                value={userProfile.name}
                style={{
                    border: '0px',
                    background: '#EDEDED',
                    width: '100%',
                    height: '44px',
                    padding: '0 5px',
                }}
            />
            <div style={{ height: '20px', margin: '15px 0 5px 0' }}>Title</div>
            <input
                onChange={(e) =>
                    setNewUserProfile(new UserProfile({ ...userProfile, title: e.target.value }))
                }
                value={userProfile.title}
                style={{
                    border: '0px',
                    background: '#EDEDED',
                    width: '100%',
                    height: '44px',
                    padding: '0 5px',
                }}
            />
            <div style={{ height: '20px', margin: '15px 0 5px 0' }}>Email*</div>
            <input
                disabled={!!userProfile.id}
                type="email"
                onChange={(e) =>
                    setNewUserProfile(
                        new UserProfile({ ...userProfile, email: e.target.value.toLowerCase() })
                    )
                }
                value={userProfile.email}
                style={{
                    border: '0px',
                    background: '#EDEDED',
                    width: '100%',
                    height: '44px',
                    padding: '0 5px',
                }}
            />
            <div style={{ height: '20px', margin: '15px 0 5px 0' }}>Roles*</div>
            <InputMultiple
                onSelect={(selected) =>
                    setNewUserProfile(new UserProfile({ ...userProfile, roles: selected }))
                }
                value={userProfile.roles}
                values={Object.keys(ROLES)}
                getName={(key) => ROLES[key || '']}
            />
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    onClick={() => {
                        setNewUserProfile(new UserProfile({ tenantId: tenant }));
                        setModalIsOpen(false);
                    }}
                    theme="cancel"
                    style={{ width: '70px', fontSize: '16px', justifyContent: 'space-around' }}
                >
                    <ArrowBackIcon sx={{ fontSize: 14 }} /> Cancel
                </Button>
                <Button style={{ width: '149px', fontSize: '16px' }} onClick={() => onSave(blob)}>
                    {userProfile.id ? 'Save Changes' : 'Add User'}
                </Button>
            </div>
        </div>
    );
};
